import { useJobillaOrgId } from '@/hooks'
import { usePrivateChannel } from '@/lib'
import { CampaignModel, CandidateModel } from '@/ui'
import {
	broadcastChannel,
	candidateProfileEvent,
	pipelineStageShowQueryKey,
	PipelineStageShowQueryResponse,
	useCampaignsShow,
	useCandidateNoteStore,
	usePipelineStageMove,
	usePipelineStageShow,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

export type UseCandidatesReturns = {
	campaign: CampaignModel
	candidates: CandidateModel[]
	selectedCandidate: CandidateModel | null
	stages: Array<{
		id: string
		name: string
		locked?: boolean
	}>
	handleCandidateMove: (candidateIds: string[], stageId: string) => void
	handleNoteSave: (candidateId: string, content: string) => Promise<void>
	isLoading: boolean
	refetchCandidates: () => Promise<void>
	unreadEmailCount: number
	unreadWhatsAppCount: number
}

export function useCandidates(campaignId: string, selectedCandidateId: string | null) {
	const campaign = useCampaignsShow(campaignId)

	const candidatesByStages = usePipelineStageShow(campaignId, {
		refetchInterval: 30_000,
	})

	const organisationId = useJobillaOrgId()
	usePrivateChannel(
		organisationId ? broadcastChannel.OrganisationCandidateProfile({ organisation: organisationId }) : null,
		{ [candidateProfileEvent.Updated]: [pipelineStageShowQueryKey(campaignId)] },
	)

	const mappedCandidates = useMemo((): CandidateModel[] => {
		if (!candidatesByStages.data?.candidates) {
			return []
		}

		return candidatesByStages.data.candidates
			.filter(candidate => candidate.name)
			.map(candidate => {
				const questions = candidate.formSubmission?.questions ?? []

				return {
					id: candidate.id ?? '',
					submissionId: candidate.formSubmission?.id ?? '',
					name: candidate.name ?? '',
					email: candidate.email ?? '',
					phone: candidate.phone ?? '',
					score: candidate.formSubmission?.score ?? null,
					disqualificationReason: candidate.disqualificationReason ?? null,
					response: questions.map(q => ({
						id: q.position,
						question: q.question,
						answer: q.answers,
						options: q.options ?? undefined,
					})),
					additionalQuestions: candidate.formSubmission?.additionalQuestions ?? [],
					createdAt: candidate.createdAt ? new Date(candidate.createdAt) : new Date(),
					lastSubmittedAt: candidate.formSubmission?.createdAt
						? new Date(candidate.formSubmission.createdAt)
						: candidate.createdAt
						? new Date(candidate.createdAt)
						: new Date(),
					stageId: candidate.pipelineStageId,
					hasUnreadEmails: candidate.hasUnreadEmails ?? false,
					unreadEmailCount: candidate.unreadEmailCount ?? 0,
					hasUnreadWhatsApp: candidate.hasUnreadWhatsApp ?? false,
					unreadWhatsAppCount: candidate.unreadWhatsAppCount ?? 0,
					hasOrphanedStage: candidate.hasOrphanedStage ?? false,
					notesCount: candidate.notes.length,
					submissionCount: candidate.submissionCount,
					files: candidate.files,
					profileCounts: candidate.profileCounts,
					interviewBadgeState: candidate.interviewBadgeState ?? null,
					interviewBooking: candidate.latestInterviewBooking
						? {
							status: candidate.latestInterviewBooking.status,
							candidateAttended: candidate.latestInterviewBooking.candidateAttended,
							scheduledAt: new Date(candidate.latestInterviewBooking.scheduledAt),
						}
						: null,
				}
			})
	}, [candidatesByStages.data])

	const queryClient = useQueryClient()
	const { mutate: moveCandidateMutation } = usePipelineStageMove({
		onMutate: async (variables) => {
			// cancel any current fetches
			await queryClient.cancelQueries({
				queryKey: pipelineStageShowQueryKey(campaignId),
			})

			// get the previous data
			const previousData = queryClient.getQueryData(pipelineStageShowQueryKey(campaignId))

			// set the new data
			queryClient.setQueryData<PipelineStageShowQueryResponse>(
				pipelineStageShowQueryKey(campaignId),
				(old) => {
					if (!old) {
						return undefined
					}

					return {
						pipelineStages: old.pipelineStages,
						candidates: old.candidates.map((candidate) =>
							variables.data.candidateIds.includes(candidate.id)
								? {
									...candidate,
									pipelineStageId: variables.data.destination,
									hasOrphanedStage: false,
								}
								: candidate
						),
						unreadEmailCount: old.unreadEmailCount,
						unreadWhatsAppCount: old.unreadWhatsAppCount,
					}
				},
			)

			return { previousData }
		},
		onError: (err, variables, context) => {
			// The generated hook does not thread onMutate's return through as
			// TContext, so the rollback value has to be named here.
			const previousData = (context as { previousData?: PipelineStageShowQueryResponse } | undefined)
				?.previousData
			if (previousData) {
				queryClient.setQueryData(
					pipelineStageShowQueryKey(campaignId),
					previousData,
				)
			}
		},
		onSettled: () => {
			// Mark query as stale without triggering immediate refetch.
			// The optimistic update already has the correct state - refetching
			// can cause a flicker if server data hasn't propagated yet.
			queryClient.invalidateQueries({
				queryKey: pipelineStageShowQueryKey(campaignId),
				refetchType: 'none',
			})
		},
	})

	const handleCandidateMove = (candidateIds: string[], stageId: string) => {
		moveCandidateMutation({
			campaign: campaignId,
			data: {
				candidateIds,
				destination: stageId,
			},
		})
	}

	const candidateNoteMutation = useCandidateNoteStore()

	const handleNoteSave = (candidateId: string, content: string): Promise<void> => {
		return new Promise((resolve, reject) => {
			candidateNoteMutation.mutate(
				{ candidate: candidateId, data: { content } },
				{
					onSuccess: async () => {
						// Wait for the refetch to complete before resolving
						try {
							await queryClient.refetchQueries({
								queryKey: pipelineStageShowQueryKey(campaignId),
							})
							resolve()
						} catch (error) {
							reject(error)
						}
					},
					onError: (error) => {
						reject(error)
					},
				},
			)
		})
	}

	return {
		campaign,
		candidates: mappedCandidates,
		selectedCandidate: selectedCandidateId
			? mappedCandidates.find(candidate => candidate.id === selectedCandidateId)
			: null,
		stages: candidatesByStages.data?.pipelineStages ?? [],
		handleCandidateMove,
		handleNoteSave,
		isLoading: candidatesByStages.isLoading,
		refetchCandidates: async () => {
			await candidatesByStages.refetch()
		},
		unreadEmailCount: candidatesByStages.data?.unreadEmailCount ?? 0,
		unreadWhatsAppCount: candidatesByStages.data?.unreadWhatsAppCount ?? 0,
	}
}
